import React from "react";
import {
  Box,
  Typography,
  FormControl,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SearchResult from "../../components/SearchResult";
import ApiService from "../../services/api-service";
import Loader from "./../../components/Loader";
import PageHeader from "../../components/PageHeader";
import AddMusicToPlaylist from "../../components/AddMusicToPlaylist";
import { useTranslation } from "react-i18next";
import './index.css'; // Ensure this path is correct
import { checkElasticSearchHealth } from '../../utils/checkElasticSearch';

export default function Search({ showClose }) {
  const { t } = useTranslation("common");
  const [showPlaylist, setShowPlaylist] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [selectedId, setSelectedAlbum] = React.useState();
  const [focus, setFocus] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [user, setUser] = React.useState([]);
  const [playlist, setPlaylist] = React.useState([]);
  const [music, setMusic] = React.useState([]);
  const [keyword, setKeyword] = React.useState("");
  const [isTyping, setIsTyping] = React.useState(false);
  const typingTimerRef = React.useRef(null);
  const doneTypingInterval = 1000;

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, []);

  const handleSearch = async (keyword) => {
    console.log("[Search] handleSearch called with keyword:", keyword);
    if (keyword) {
      try {
        setLoading(true);
        const response = await ApiService.SEARCH(keyword);
        console.log("[Search] API Response:", response);
        setMusic([...response["result"]["music"]]);
        setPlaylist([...response["result"]["studio"]]);
        setUser([...response["result"]["artist"]]);
        setLoading(false);
      } catch (error) {
        console.error("[Search] Error:", error);
        setLoading(false);
        
        // Check if it's an Elasticsearch connectivity issue
        if (error.response && (error.response.status === 503 || error.response.status === 500)) {
          console.error("[Search] Elasticsearch may be down. Running diagnostic...");
          checkElasticSearchHealth();
          
          // Show user-friendly error
          setMusic([]);
          setPlaylist([]);
          setUser([]);
          // You might want to show a notification here
          // notifier.error("Search is temporarily unavailable. Please try again later.");
        }
      }
    }
  };

  // Use useCallback to prevent recreating the function on every render
  const doneTyping = React.useCallback(() => {
    console.log("[Search] doneTyping called, keyword:", keyword);
    setFocus(true);
    if (keyword.length >= 1) {
      handleSearch(keyword);
    }
  }, [keyword]);

  const onKeyup = React.useCallback(() => {
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(doneTyping, doneTypingInterval);
    setIsTyping(false);
  }, [doneTyping]);

  const onKeydown = React.useCallback(() => {
    clearTimeout(typingTimerRef.current);
    setIsTyping(true);
  }, []);

  React.useEffect(() => {
    // Prevent body scroll when search results are open
    if (focus && (user.length > 0 || music.length > 0 || playlist.length > 0)) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [focus, user.length, music.length, playlist.length]);

  return (
    <>
      {/* Search Results - SearchResult component handles its own portal and backdrop */}
      {focus && !loading && (user.length > 0 || music.length > 0 || playlist.length > 0) && (
        <SearchResult
          handleClose={() => {
            setKeyword("");
            setFocus(false);
          }}
          keyword={keyword}
          users={user}
          musics={music}
          playlists={playlist}
          onShowAddPlaylist={(musicId, show) => {
            setSelectedAlbum(musicId);
            setShowPlaylist(show);
          }}
          style={{ maxHeight: '80vh', overflowY: 'auto' }}
        />
      )}
      
      <Box
        sx={{
          display: "flex",
          justifyContent: 'center',
          width: "100%",
          minWidth: "250px",
          position: "relative",
          height: "auto",
          paddingLeft: { xs: 0, sm: "18px" },
          paddingRight: { xs: 0, sm: "18px" },
          margin: "0 auto",
        }}
      >

      <div className={`search-input-container`}>
        <OutlinedInput
          sx={{
            width: "100%",
            borderRadius: "4px",
            height: "36px",
            fontFamily: "var(--font-display)",
            fontSize: "12px",
            letterSpacing: "0.5px",
            color: "var(--hud-text, #E8E8EE)",
            background: "rgba(12, 12, 18, 0.8)",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--hud-border, #16161F)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--hud-border, #16161F)",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "var(--hud-accent-neon, rgba(255, 238, 32,0.4))",
              borderWidth: "1px",
            },
            "&.Mui-focused": {
              boxShadow: "0 0 12px rgba(255, 238, 32,0.08), inset 0 0 12px rgba(255, 238, 32,0.03)",
            },
            "& input::placeholder": {
              color: "var(--hud-text-muted, #6A6A80)",
              opacity: 1,
            },
          }}
          id="search-input"
          className={`search-input ${focus ? "focused" : ""}`}
          placeholder={t("header.search")}
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setFocus(true);
          }}
          onKeyUp={onKeyup}
          onKeyDown={onKeydown}
          startAdornment={
            <InputAdornment position="start" id="_search_icon">
              {showClose ?? <SearchIcon sx={{ color: "var(--hud-text-dim, #3A3A50)", width: 18, height: 18 }} />}
            </InputAdornment>
          }
          aria-describedby="outlined-weight-helper-text"
          inputProps={{
            "aria-label": "Search Artist, Song, Studio Name",
          }}
          autoComplete="off"
        />
      </div>

        {/* Removed loader to prevent yellow line when typing */}
        {showPlaylist ? (
          <AddMusicToPlaylist
            musicId={selectedId}
            open={showPlaylist}
            handleClose={() => {
              setShowPlaylist(false);
            }}
          />
        ) : null}
      </Box>
    </>
  );
}
